import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles';
import CtaButton from '../components/CtaButton';

const TimelineStep = ({ number, title, description, timeline, objective, delay }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className="timeline-step"
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: '64px',
        marginBottom: '120px',
        position: 'relative',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition:
          'opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1s cubic-bezier(0.2, 0.8, 0.2, 1)',
      }}
    >
      <div style={styles.stepNumber}>{number}</div>
      <div
        className="timeline-content"
        style={{
          borderTop: '2px solid #1B2B3E',
          paddingTop: '32px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '120px',
        }}
      >
        <div>
          <h3 style={styles.stepTitle}>{title}</h3>
          <p style={styles.stepDesc}>{description}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div
            style={{
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(27, 43, 62, 0.1)',
            }}
          >
            <span style={styles.detailLabel}>Timeline</span>
            <span style={styles.detailValue}>{timeline}</span>
          </div>
          <div
            style={{
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(27, 43, 62, 0.1)',
            }}
          >
            <span style={styles.detailLabel}>Objective</span>
            <span style={styles.detailValue}>{objective}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExpectCard = ({ title, description }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <h4 style={styles.expectCardTitle}>{title}</h4>
    <p style={styles.expectCardDesc}>{description}</p>
  </div>
);

const ProcessPage = () => {
  const navigate = useNavigate();
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      <header
        style={{
          paddingTop: '120px',
          paddingBottom: '64px',
          opacity: heroVisible ? 1 : 0,
          transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
          transition:
            'opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <div style={styles.container}>
          <h1 style={styles.heroTitleUpper}>
            The Path To<br />Better Credit.
          </h1>
          <p style={styles.heroSub}>
            We believe in transparency. Our multi-phase correction strategy is designed for
            efficiency and legal compliance at every step.
          </p>
        </div>
      </header>

      <section
        style={{
          ...styles.container,
          marginTop: '120px',
          paddingBottom: '180px',
        }}
      >
        <TimelineStep
          number="01"
          title="Strategic Audit"
          description="We conduct a granular analysis of your credit history from Equifax, Experian, and TransUnion. We don't just look for errors; we look for leverage."
          timeline="Days 1-5"
          objective="Identify unverified & obsolete debt reporting."
          delay={0.1}
        />
        <TimelineStep
          number="02"
          title="Bureau Challenge"
          description="We draft and issue formal challenges to the credit bureaus using FCRA-compliant frameworks. We demand validation for every negative mark on your record."
          timeline="Days 7-35"
          objective="Force removal of inaccurate data points."
          delay={0.2}
        />
        <TimelineStep
          number="03"
          title="Rebuilding"
          description="Correction is only half the battle. We advise on strategic credit mix adjustments and credit building tools to accelerate your score's upward momentum."
          timeline="Days 35-60+"
          objective="Establish positive payment history and utilization."
          delay={0.3}
        />
      </section>

      <section style={styles.expectionsSection}>
        <div style={styles.container}>
          <h2 style={styles.expectionsTitle}>What To Expect.</h2>
          <div className="expect-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '64px' }}>
            <ExpectCard
              title="Bi-Weekly Updates"
              description="You will never wonder about your status. We provide consistent updates as bureau responses arrive at our processing center."
            />
            <ExpectCard
              title="Secure Portal"
              description="24/7 access to your personal dashboard where you can see every item challenged and every item deleted in real-time."
            />
            <ExpectCard
              title="Legal Compliance"
              description="We operate strictly within the Credit Repair Organizations Act (CROA). No false promises, just results-driven strategy."
            />
          </div>

          <div style={{ marginTop: '80px' }}>
            <CtaButton
              text="Begin your consultation"
              light
              onClick={() => navigate('/contact')}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;
